import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ConditionalRender } from '@ui';
import { PermissionRequiredModal } from '@features/components/CaseFormBlocks/components';
import {
  CustomerPermissionEnum,
  CustomerPermissionOnly,
  CustomerRoleEnum,
  CustomerRoleOnly,
  CustomerRoleT,
} from '@/api/apis/customer';
import { UpdateTXUserFormValues } from '@/types/customer';
import { RoleSelector } from './RoleSelector';
import { PermissionSelector } from './PermissionSelector';
import { useRoleFormLogic } from './useRoleFormLogic';

type TXUserRoleUpdateFormProps = {
  isReadOnly?: boolean;
  index: number;
  txCenterName: string;
};

const isRoleOnly = (v: CustomerRoleT): v is CustomerRoleOnly =>
  Object.values(CustomerRoleEnum).includes(v as CustomerRoleEnum);

const isPermissionOnly = (v: CustomerRoleT): v is CustomerPermissionOnly =>
  Object.values(CustomerPermissionEnum).includes(v as CustomerPermissionEnum);

export const TXUserRoleUpdateForm = ({ isReadOnly = false, txCenterName, index }: TXUserRoleUpdateFormProps) => {
  const { control, setValue, trigger, getValues } = useFormContext<UpdateTXUserFormValues>();

  const rolesAllRaw = useWatch({
    control,
    name: `customerInTxCenters.${index}.roles`,
  }) as CustomerRoleT[] | undefined;

  const rolesAll = useMemo<CustomerRoleT[]>(() => rolesAllRaw ?? [], [rolesAllRaw]);
  const roleValues = useMemo<CustomerRoleOnly[]>(() => rolesAll.filter(isRoleOnly), [rolesAll]);
  const permissionValues = useMemo<CustomerPermissionOnly[]>(() => rolesAll.filter(isPermissionOnly), [rolesAll]);

  const setRolesAll = useCallback(
    (next: CustomerRoleT[]) => {
      setValue(`customerInTxCenters.${index}.roles`, next, { shouldValidate: true, shouldDirty: true });
    },
    [index, setValue],
  );

  const updateRoles = useCallback(
    (nextRolesOnly: CustomerRoleOnly[]) => {
      setRolesAll([...nextRolesOnly, ...permissionValues]);
    },
    [permissionValues, setRolesAll],
  );

  const updatePermissions = useCallback(
    (nextPermsOnly: CustomerPermissionOnly[]) => {
      const currentAll = getValues(`customerInTxCenters.${index}.roles`) as CustomerRoleT[] | undefined;
      const currentRoles = (currentAll ?? []).filter(isRoleOnly);
      setRolesAll([...currentRoles, ...nextPermsOnly]);
    },
    [index, getValues, setRolesAll],
  );

  const triggerBoth = useCallback(async () => {
    const ok = await trigger(`customerInTxCenters.${index}.roles` as const);
    return [ok, true] as [boolean, boolean];
  }, [trigger, index]);

  const { handleRoleChange, handlePermissionChange, permissionRequiredModalData, setPermissionRequiredModalData } =
    useRoleFormLogic({
      roleValues,
      permissionValues,
      updateRoles,
      updatePermissions,
      triggerBoth,
    });

  return (
    <div className="flex flex-col gap-2">
      <span className="text-hs text-base-5 m-0">{txCenterName || 'TX Center'}</span>
      <RoleSelector
        name={`customerInTxCenters.${index}.roles`}
        roleValues={roleValues}
        handleRoleChange={handleRoleChange}
        isReadOnly={isReadOnly}
      />
      <PermissionSelector
        name={`customerInTxCenters.${index}.roles`}
        roleValues={roleValues}
        permissionValues={permissionValues}
        handlePermissionChange={handlePermissionChange}
        isReadOnly={isReadOnly}
      />
      <ConditionalRender show={Boolean(permissionRequiredModalData)}>
        <PermissionRequiredModal
          data={permissionRequiredModalData!}
          onClose={() => setPermissionRequiredModalData(null)}
        />
      </ConditionalRender>
    </div>
  );
};
